import { useEffect, useState, type ReactNode } from "react";
import {
  BellRing,
  Bike,
  ChefHat,
  Hourglass,
  Inbox,
  LayoutGrid,
  Printer,
  Receipt,
  Users,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import { ToggleChipButton } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import type {
  FloorPlateCounts,
  TableGridArea,
  TableGridEntry,
} from "@/lib/api/types";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";
import { useMoney } from "@/shared/ui";
import { ServiceRequestBadges } from "../serviceRequests/ServiceRequestBadges";
import type { ServiceRequest } from "../serviceRequests/serviceRequests";
import {
  busyCount,
  floorCard,
  floorTileFacts,
  tableAge,
  type FloorCard,
  type FloorCardKind,
  type FloorTileFacts,
} from "../lib/floorTile";

/**
 * The PAPER look's floor — the 2026-10 redesign's `pos` page (Tables and
 * Kitchen views), drawn on both floors: the till's (`TableGridPage`) and the
 * back office's live view (`FloorLiveView`). Presentational only: each page
 * keeps its own data read, permission checks and tap routing, and hands them in.
 *
 * Rendered only while PAPER is the look (and, on the till, the till is light —
 * `usePaperTill`), because the look's stylesheet yields to `.pos-dark`. Every
 * other look keeps the tile its artboard was measured against.
 *
 * One tile size (2026-10-03, founder): the compact/large switch is gone and
 * with it the per-device preference it stored.
 */

/**
 * The clock the age rings read. One tick a minute for the whole floor — the
 * ring's resolution is a minute, and a per-tile timer would be dozens.
 */
export function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/**
 * An area's badge. Areas are store data with no icon of their own, so the
 * badge is read off the name: a bar, a takeaway/delivery counter, or a dining
 * room for everything else. Matched in both languages, lower-cased.
 */
export function areaBadgeId(name: string): DoodleBadgeId {
  const value = name.toLowerCase();
  if (/\bbar\b|بار/.test(value)) return "wine";
  if (/take ?away|pick ?up|delivery|سفري|تيك|استلام|توصيل|دليفري/.test(value)) {
    return "foodbag";
  }
  return "cutlery";
}

/**
 * THE TABS AT THE TOP OF THE FLOOR — the redesign's segmented "Tables N |
 * Kitchen N". Each page decides which tabs exist (the Kitchen one only for a
 * session that may read the board, on a store that runs it) and what a tab
 * does; this draws them. A count of zero draws no pill, as the design does.
 */
export interface PaperFloorTab<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
  count?: number;
  /** `alert` = the red pill (something waits on the kitchen), `info` = blue. */
  tone?: "neutral" | "alert" | "info";
  testId?: string;
}

export function PaperFloorTabs<T extends string>({
  tabs,
  value,
  onChange,
  end,
}: {
  tabs: readonly PaperFloorTab<T>[];
  value: T;
  onChange: (next: T) => void;
  /**
   * B6 · P24 (D-35): commands drawn at the END of the tab bar — the Delivery
   * tab's "New delivery order" / "New pickup order". The page decides when
   * they show; absent, the bar is exactly the tab group it always was.
   */
  end?: ReactNode;
}) {
  const t = useTranslate();
  const group = (
    <div
      role="tablist"
      aria-label={t((m) => m.paperFloor.tabsLabel)}
      data-ui="floor-tabs"
      className="no-print inline-flex max-w-full flex-wrap self-start"
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        const Icon = tab.icon;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-active={active ? "" : undefined}
            data-ui="floor-tab"
            data-testid={tab.testId}
            onClick={() => onChange(tab.value)}
            className="inline-flex items-center"
          >
            <Icon aria-hidden="true" size={15} strokeWidth={1.75} />
            <span>{tab.label}</span>
            {tab.count ? (
              <span
                data-ui="floor-tab-count"
                data-tone={tab.tone ?? "neutral"}
                className="text-center"
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
  if (!end) return group;
  return (
    <div data-ui="floor-tabs-row" className="no-print flex w-full flex-wrap items-center gap-2">
      {group}
      <div data-ui="floor-tabs-end" className="ms-auto flex flex-wrap items-center gap-2">
        {end}
      </div>
    </div>
  );
}

/** The floor's tab icons, the redesign's; Delivery & takeaway is B6 · P24's. */
export const FLOOR_TAB_ICONS = { tables: LayoutGrid, kitchen: ChefHat, delivery: Bike } as const;

export function PaperFloorToolbar({
  areas,
  areaFilter,
  onAreaFilter,
  children,
}: {
  areas: readonly TableGridArea[];
  areaFilter: string | null;
  onAreaFilter: (areaId: string | null) => void;
  /** Extra filters the page owns (the till's cheque-out filter). */
  children?: ReactNode;
}) {
  const t = useTranslate();
  const all = areas.flatMap((area) => area.tables);
  const chip = (id: string | null, label: string, count: string) => (
    <ToggleChipButton
      key={id ?? "all"}
      active={areaFilter === id}
      aria-pressed={areaFilter === id}
      onClick={() => onAreaFilter(id)}
      data-testid={`floor-area-${id ?? "all"}`}
      className="inline-flex items-center gap-1.5"
    >
      {label}
      {/* Busy over total — "how full is it", the question a host asks first. */}
      <span>{count}</span>
    </ToggleChipButton>
  );
  return (
    <div data-ui="floor-toolbar" className="no-print flex flex-wrap items-center gap-1.5">
      {areas.length > 1 ? (
        <>
          {chip(null, t((m) => m.pos.allAreas), `${busyCount(all)}/${all.length}`)}
          {areas.map((area) =>
            chip(
              area.areaId,
              area.name,
              `${busyCount(area.tables)}/${area.tables.length}`,
            ),
          )}
        </>
      ) : null}
      {children}
    </div>
  );
}

/**
 * The floor while the grid loads: the shape of what is coming — an area head,
 * then table tiles in the floor's own grid — rather than generic full-width
 * bars, which read as a broken page and sat above the tabs (founder, 2026-10-28).
 */
export function PaperFloorSkeleton({ label }: { label: string }) {
  const bar = "animate-pulse rounded-full bg-[color:var(--surface-alt)]";
  return (
    <div aria-busy="true" aria-live="polite" aria-label={label} data-testid="floor-skeleton" className="flex flex-col gap-6">
      {[8, 4].map((tiles, area) => (
        <section key={area} data-ui="floor-area" aria-hidden="true" className="flex flex-col gap-3 pt-2">
          <div data-ui="floor-area-head" className="flex items-center gap-2.5 pb-2">
            <span className={`size-[30px] ${bar}`} />
            <span className={`h-4 w-28 ${bar}`} />
            <span className={`h-3 w-20 ${bar}`} />
          </div>
          <div data-ui="floor-grid" className="grid">
            {Array.from({ length: tiles }, (_, i) => (
              <div
                key={i}
                className="flex min-h-[180px] flex-col justify-between rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3 ps-4"
              >
                <span className={`h-5 w-10 ${bar}`} />
                <span className={`h-3 w-16 ${bar}`} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** A section under a badge heading: an area of tables. */
function PaperFloorSection({
  badge,
  title,
  info,
  testId,
  children,
}: {
  badge: DoodleBadgeId;
  title: string;
  info: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section data-ui="floor-area" data-testid={testId} className="flex flex-col gap-3 pt-2">
      <div data-ui="floor-area-head" className="flex items-center gap-2.5 pb-2">
        <DoodleBadge id={badge} size={30} animate={false} />
        <h2 data-ui="floor-area-title">{title}</h2>
        <span data-ui="floor-area-count">{info}</span>
      </div>
      <div data-ui="floor-grid" className="grid">
        {children}
      </div>
    </section>
  );
}

export function PaperAreaSection({
  area,
  lateCount,
  children,
}: {
  area: TableGridArea;
  /** The area's late cards (2026-10 floor), for "· late: N"; omitted, no count. */
  lateCount?: number;
  children: ReactNode;
}) {
  const t = useTranslate();
  const counts = { busy: busyCount(area.tables), tables: area.tables.length };
  return (
    <PaperFloorSection
      badge={areaBadgeId(area.name)}
      title={area.name}
      // The redesign's "X of Y busy". An area carries no description of its
      // own, so the count is the whole line.
      info={
        lateCount
          ? t((m) => m.paperFloor.areaBusyLate, { ...counts, late: lateCount })
          : t((m) => m.paperFloor.areaBusy, counts)
      }
    >
      {children}
    </PaperFloorSection>
  );
}

/**
 * How long ago a closed off-premise bill left (T8) — "closed 12 min ago" — in
 * the till's words. Minutes, then hours: a bag out for a day is a bag that
 * was never collected, and that reads clearly enough as hours.
 */
export function closedSinceLabel(
  closedAt: string,
  now: number,
  t: ReturnType<typeof useTranslate>,
): string {
  const { minutes } = tableAge(closedAt, now);
  return minutes >= 60
    ? t((m) => m.pos.offPremiseClosedSinceHours, {
        count: Math.floor(minutes / 60),
      })
    : t((m) => m.pos.offPremiseClosedSince, { count: minutes });
}

/** The most served dots a tile draws; the count beside them says the rest. */
const MAX_DOTS = 12;

/**
 * WHAT THE KITCHEN HAS FOR THIS TABLE, in words (audit round 2, Phase 3):
 * «2 في التحضير · 1 جاهز», off the bill's frozen `FloorPlateCounts`, then
 * «متأخر» when the SERVER says a preparing plate is over its own target
 * (`latePreparing`, the kitchen board's rule, judged at the read).
 *
 * - The words are the kitchen board's own lane words, so a waiter reads the
 *   same thing the cook does. Counted through `countedKey` — the noun is
 *   elided, so one and two are said in words (the B67 lesson).
 * - A zero is not printed: "0 ready" is noise on a tile read at a glance.
 * - «متأخر» is a WORD with its own hook. Colour is never the only carrier, and
 *   here it is secondary: the tile's edge keeps its status colour.
 * - Nothing here keeps time. The marker comes and goes with the floor's own
 *   reads (every revision, every focus), never with a clock on the device.
 *
 * Drawn on the PAPER tile and on the dark till's tile; `className` is for the
 * dark till only, whose tile is styled in place (the hooks' look lives in
 * `_paper-till.css`, which yields to `.pos-dark`).
 */
export function FloorTileKitchenLine({
  plates,
  late,
  testId,
  className,
  lateClassName,
}: {
  plates: FloorPlateCounts | null;
  late: boolean;
  testId: string;
  className?: string;
  lateClassName?: string;
}) {
  const t = useTranslate();
  const parts: string[] = [];
  if (plates && plates.preparing > 0) {
    parts.push(
      t(
        countedKey(plates.preparing, {
          one: (m) => m.paperFloor.kitchenPreparingOne,
          two: (m) => m.paperFloor.kitchenPreparingTwo,
          many: (m) => m.paperFloor.kitchenPreparing,
        }),
        { count: plates.preparing },
      ),
    );
  }
  if (plates && plates.ready > 0) {
    parts.push(
      t(
        countedKey(plates.ready, {
          one: (m) => m.paperFloor.kitchenReadyOne,
          two: (m) => m.paperFloor.kitchenReadyTwo,
          many: (m) => m.paperFloor.kitchenReady,
        }),
        { count: plates.ready },
      ),
    );
  }
  if (parts.length === 0 && !late) return null;
  return (
    <span
      data-ui="floor-tile-kitchen"
      data-testid={`${testId}-kitchen`}
      className={["tabular-nums", className].filter(Boolean).join(" ")}
    >
      {parts.join(" · ")}
      {late ? (
        <>
          {parts.length > 0 ? " · " : null}
          <span
            data-ui="floor-tile-late"
            data-testid={`${testId}-late`}
            className={lateClassName}
          >
            {t((m) => m.paperFloor.late)}
          </span>
        </>
      ) : null}
    </span>
  );
}

/**
 * One table, the redesign's anatomy: a status EDGE, the name and seats, a ring
 * showing how long it has been seated, then what it is doing, what it owes, what
 * has been paid, a dot per plate (filled once served) and anything that needs a
 * person. Colour is never the only carrier — the status is also a word, and the
 * ring also a number.
 */
export function PaperFloorTile({
  table,
  facts = floorTileFacts(table),
  sub,
  now,
  canOpen,
  disabled,
  onOpen,
  testIdPrefix,
  offPremise,
  asks,
  dispatch,
}: {
  table: TableGridEntry;
  facts?: FloorTileFacts;
  /** The table's open diner asks (D132), oldest first — a badge each. */
  asks?: readonly ServiceRequest[];
  /** Replaces the seats line — the Takeaway section's bill number. */
  sub?: string;
  now: number;
  /** `INVOICE_OPEN`: whether a free table offers "tap to open". */
  canOpen: boolean;
  disabled: boolean;
  onOpen: () => void;
  /** `table-` on the till, `floor-tile-` in the back office — each screen's own test ids. */
  testIdPrefix: string;
  /**
   * T8 — a bill at no table: the platform that brought it (its NAME, for a
   * small badge) and, when the bill is CLOSED, when it closed — the ring then
   * counts from the close, not the open, because "how long has this bag been
   * out" is the question, and the status reads "out · unpaid" over the owed
   * amount with the tap-to-settle hint under it.
   */
  offPremise?: {
    platform: string | null;
    closedAt: string | null;
    /** T13 — the platform's own order number, on the badge beside the platform. */
    ref?: string | null;
    /** T16 — who the bag left with, on a CLOSED tile. */
    handedTo?: string | null;
  };
  /**
   * Driver backend B3 · P13 — a delivery bag's driver line (who has it, where
   * it is), under the status. Display only: the tile is a button, so the
   * assign control is a sibling the caller renders outside it.
   */
  dispatch?: ReactNode;
}) {
  const t = useTranslate();
  const money = useMoney();
  const testId = `${testIdPrefix}${table.code}`;
  // A table is drawn as the 2026-10 floor card; a bill at no table (T8) keeps
  // the tile below, whose ring counts from the close.
  if (!offPremise) {
    return (
      <PaperFloorCard
        table={table}
        facts={facts}
        card={floorCard(table, facts, asks, now)}
        asks={asks}
        canOpen={canOpen}
        disabled={disabled}
        onOpen={onOpen}
        testId={testId}
      />
    );
  }
  const ageFrom = offPremise?.closedAt ?? facts.oldestOpenedAt;
  const age = ageFrom ? tableAge(ageFrom, now) : null;
  const outClosed = Boolean(offPremise?.closedAt);

  // A quiet table whose bill has a pre-bill check leads with the check
  // (round 5): amber while it still is the bill, red once the bill moved.
  const statusLabel = outClosed
    ? t((m) => m.pos.offPremiseOutStatus)
    : facts.check
    ? facts.check.state === "stale"
      ? t((m) => m.preBillCheck.tileStale)
      : t((m) => m.preBillCheck.tilePrinted)
    : facts.status === "waiting"
      ? // The design's "Waiting", with HOW MANY guest requests wait on it —
        // the count the old floor's tile and bar carried (audit, 2026-10-03).
        facts.pendingCount > 0
        ? t(
            countedKey(facts.pendingCount, {
              one: (m) => m.pos.pendingCountOne,
              two: (m) => m.pos.pendingCountTwo,
              few: (m) => m.pos.pendingCountFew,
              many: (m) => m.pos.pendingCount,
            }),
            { count: facts.pendingCount },
          )
        : t((m) => m.paperFloor.statusWaiting)
      : facts.status === "ready"
        ? t((m) => m.paperFloor.statusReady)
        : facts.status === "preparing"
          ? t((m) => m.paperFloor.statusPreparing)
          : facts.status === "eating"
            ? t((m) => m.paperFloor.statusEating)
            : facts.status === "cheque"
              ? facts.chequeCount > 1
                ? t(
                    countedKey(facts.chequeCount, {
                      one: (m) => m.pos.tableChequeCountOne,
                      two: (m) => m.pos.tableChequeCountTwo,
                      few: (m) => m.pos.tableChequeCountFew,
                      many: (m) => m.pos.tableChequeCount,
                    }),
                    { count: facts.chequeCount },
                  )
                : t((m) => m.pos.tableChequeOut)
              : t((m) => m.pos.tableOccupied);

  const live = facts.open !== null;
  // What the table still owes; "Paid" once a payment has covered it.
  const owes = facts.due > 0.004;
  const dueLabel =
    !owes && facts.paidSoFar > 0
      ? t((m) => m.paperFloor.paid)
      : t((m) => m.paperFloor.due, { amount: money(owes ? facts.due : 0) });

  const plates = facts.plates;
  const dots =
    live && plates && plates.chargeable > 0
      ? Array.from({ length: Math.min(MAX_DOTS, plates.chargeable) }, (_, index) => index < plates.served)
      : null;

  const openFor = age
    ? age.minutes >= 60
      ? t((m) => m.pos.openForHours, { count: Math.floor(age.minutes / 60) })
      : t((m) => m.pos.openForMinutes, { count: age.minutes })
    : undefined;

  /*
   * The bill's number (#51), quiet beside the seats, on a table with ONE live
   * bill — waiters match it to the chit and the receipt (audit, 2026-10-03).
   * A table with several keeps its "N bills" badge; an off-premise tile already
   * names its bill in `sub`.
   */
  const liveBills = [...table.openInvoices, ...table.closedInvoices];
  const billNumber =
    sub === undefined && liveBills.length === 1 ? `#${liveBills[0].dailySequence}` : null;

  const seatsLabel =
    sub ??
    (table.seats !== null
      ? t(
          countedKey(table.seats, {
            one: (m) => m.manage.tablesSeatsOne,
            two: (m) => m.manage.tablesSeatsTwo,
            few: (m) => m.manage.tablesSeatsFew,
            many: (m) => m.manage.tablesSeats,
          }),
          { count: table.seats },
        )
      : null);

  return (
    <button
      type="button"
      data-ui="floor-tile"
      data-state={facts.status}
      data-check={facts.check?.state}
      data-late={live && facts.late ? "true" : undefined}
      data-testid={testId}
      disabled={disabled}
      onClick={onOpen}
      className="relative flex w-full flex-col items-stretch gap-2 overflow-hidden text-start"
    >
      <span aria-hidden="true" data-ui="floor-tile-edge" className="absolute inset-y-0 start-0" />
      <span className="flex items-center gap-2">
        <span data-ui="floor-tile-name">{table.name}</span>
        {offPremise?.platform ? (
          <span
            data-ui="floor-tile-badge"
            data-tone="info"
            data-testid={`${testId}-platform`}
            className="whitespace-nowrap"
          >
            <bdi>{offPremise.platform}</bdi>
            {offPremise.ref ? <bdi dir="ltr"> · {offPremise.ref}</bdi> : null}
          </span>
        ) : null}
        {seatsLabel ? (
          <span data-ui="floor-tile-seats" className="whitespace-nowrap">
            {seatsLabel}
          </span>
        ) : null}
        {billNumber ? (
          <span
            data-ui="floor-tile-seats"
            data-kind="bill"
            data-testid={`${testId}-bill-number`}
            className="whitespace-nowrap tabular-nums"
          >
            {billNumber}
          </span>
        ) : null}
        <span className="flex-1" />
        {age ? (
          <span
            data-ui="floor-tile-age"
            data-tone={age.tone}
            title={openFor}
            aria-label={openFor}
            className="relative shrink-0"
            style={{ ["--age" as string]: `${age.percent}%` }}
          >
            <span aria-hidden="true" data-ui="floor-tile-age-value" className="absolute flex items-center justify-center tabular-nums">
              {/* Two digits and a unit is all the ring holds: minutes up to
                  99, then hours, then days for a bill left open overnight. */}
              {age.minutes < 100
                ? t((m) => m.pos.tileAgeMinutes, { count: age.minutes })
                : age.minutes < 24 * 60
                  ? t((m) => m.pos.tileAgeHours, { count: Math.floor(age.minutes / 60) })
                  : t((m) => m.pos.tileAgeDays, { count: Math.floor(age.minutes / (24 * 60)) })}
            </span>
          </span>
        ) : null}
      </span>

      {facts.status === "free" ? (
        <span data-ui="floor-tile-free" className="mt-auto">
          {canOpen ? t((m) => m.paperFloor.freeTapToOpen) : t((m) => m.pos.tableFree)}
        </span>
      ) : (
        <span className="flex flex-col gap-1.5">
          <span
            data-ui="floor-tile-status"
            data-testid={facts.check ? `${testId}-check-${facts.check.state}` : undefined}
            className="flex flex-wrap items-center gap-1.5"
          >
            <span aria-hidden="true" data-ui="floor-tile-dot" />
            {statusLabel}
          </span>
          {dispatch ?? null}
          {live ? (
            <FloorTileKitchenLine plates={plates} late={facts.late} testId={testId} />
          ) : null}
          <span data-ui="floor-tile-due" className="whitespace-nowrap tabular-nums">
            {dueLabel}
          </span>
          {/* T8 — a closed bag: when it left, and that a tap takes the money.
              The same hint the dine-in cheque-out tile has always carried. */}
          {outClosed && offPremise?.closedAt ? (
            <span data-ui="floor-tile-paid" data-testid={`${testId}-closed-since`}>
              {/* T16 — WHO it left with, first: the fact the phone call is about. */}
              {offPremise.handedTo ? (
                <>
                  <bdi>{t((m) => m.pos.offPremiseHandedTo, { name: offPremise.handedTo })}</bdi>
                  {" · "}
                </>
              ) : null}
              {closedSinceLabel(offPremise.closedAt, now, t)}
              {" · "}
              {t((m) => m.pos.tableChequeOutHint)}
            </span>
          ) : null}
          {owes && facts.paidSoFar > 0 ? (
            <span data-ui="floor-tile-paid">
              {t((m) => m.paperFloor.paidSoFar, { amount: money(facts.paidSoFar) })}
            </span>
          ) : null}
          {dots && plates ? (
            <span
              data-ui="floor-tile-dots"
              title={t((m) => m.paperFloor.servedHint)}
              className="flex flex-wrap items-center"
            >
              {dots.map((served, index) => (
                <span
                  key={index}
                  aria-hidden="true"
                  data-ui="floor-tile-served-dot"
                  data-served={served ? "" : undefined}
                />
              ))}
              <span data-ui="floor-tile-served">
                {t((m) => m.paperFloor.served, {
                  served: plates.served,
                  total: plates.chargeable,
                })}
              </span>
            </span>
          ) : null}
          {/* What else needs a person, beyond the status itself: an older cheque
              still out on a live table, a party that seated itself from the QR,
              a guest who says they have paid, several bills on one table. */}
          {(live && facts.cheque) ||
          facts.selfSeated ||
          facts.hasPaymentClaim ||
          facts.cashierRequested ||
          (facts.noCaptain && !offPremise) ||
          facts.openBillCount > 1 ? (
            <span className="flex flex-wrap gap-1">
              {facts.openBillCount > 1 ? (
                <span data-ui="floor-tile-badge" data-tone="info" data-testid={`${testId}-bills`}>
                  {t(
                    countedKey(facts.openBillCount, {
                      one: (m) => m.pos.tableBillCountOne,
                      two: (m) => m.pos.tableBillCountTwo,
                      few: (m) => m.pos.tableBillCountFew,
                      many: (m) => m.pos.tableBillCount,
                    }),
                    { count: facts.openBillCount },
                  )}
                </span>
              ) : null}
              {live && facts.cheque ? (
                <span data-ui="floor-tile-badge" data-tone="warning">
                  {t((m) => m.pos.tableChequeOut)}
                </span>
              ) : null}
              {facts.selfSeated ? (
                <span data-ui="floor-tile-badge" data-tone="info" data-testid={`${testId}-self-seated`}>
                  {t((m) => m.pos.selfSeatedBadge)}
                </span>
              ) : null}
              {facts.cashierRequested ? (
                <span data-ui="floor-tile-badge" data-tone="warning" data-testid={`${testId}-cashier-requested`}>
                  {t((m) => m.pos.cashierWaitingBadge)}
                </span>
              ) : null}
              {facts.hasPaymentClaim ? (
                <span data-ui="floor-tile-badge" data-tone="danger" data-testid={`${testId}-payment-claim`}>
                  {t((m) => m.pos.paymentClaimBadge)}
                </span>
              ) : null}
              {/* D153 — last and toneless: a quiet note for the head waiter,
                  never an alarm. A dine-in table only. */}
              {facts.noCaptain && !offPremise ? (
                <span data-ui="floor-tile-badge" data-testid={`${testId}-no-captain`}>
                  {t((m) => m.captain.none)}
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
      )}
      {/* D132 — what the table has asked the floor for, on a free table too:
          a diner may call before anyone has opened a bill. */}
      {asks && asks.length > 0 ? <ServiceRequestBadges asks={asks} testId={testId} /> : null}
    </button>
  );
}

const CARD_ICON: Record<FloorCardKind, LucideIcon> = {
  waiting: Inbox,
  ready: BellRing,
  stale: Printer,
  bill: Receipt,
  prebill: Printer,
  kitchen: ChefHat,
  eating: Utensils,
  seated: Hourglass,
  free: LayoutGrid,
};

/**
 * ONE TABLE ON THE FLOOR, the 2026-10 redesign's card: the name and seats, one
 * coloured line saying what is happening and for how long, the served bar,
 * the amount, and the next step in a word. Timers stay neutral until the
 * state passes its limit, then the card gets a red ring (`floorCard`). A free
 * table shrinks to a small row. The whole card is the button: the action word
 * names what opening it is for, and the tap routes as the tile always did.
 */
function PaperFloorCard({
  table,
  facts,
  card,
  asks,
  canOpen,
  disabled,
  onOpen,
  testId,
}: {
  table: TableGridEntry;
  facts: FloorTileFacts;
  card: FloorCard;
  asks?: readonly ServiceRequest[];
  canOpen: boolean;
  disabled: boolean;
  onOpen: () => void;
  testId: string;
}) {
  const t = useTranslate();
  const money = useMoney();
  const seatsLabel =
    table.seats !== null
      ? t(
          countedKey(table.seats, {
            one: (m) => m.manage.tablesSeatsOne,
            two: (m) => m.manage.tablesSeatsTwo,
            few: (m) => m.manage.tablesSeatsFew,
            many: (m) => m.manage.tablesSeats,
          }),
          { count: table.seats },
        )
      : null;
  const seats =
    table.seats !== null ? (
      <span
        data-ui="floor-tile-seats"
        title={seatsLabel ?? undefined}
        className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums"
      >
        <Users aria-hidden="true" size={13} strokeWidth={1.75} />
        <span aria-hidden="true">{table.seats}</span>
        <span className="sr-only">{seatsLabel}</span>
      </span>
    ) : null;
  const askBadges =
    asks && asks.length > 0 ? <ServiceRequestBadges asks={asks} testId={testId} /> : null;

  if (card.kind === "free") {
    return (
      <button
        type="button"
        data-ui="floor-tile"
        data-state="free"
        data-card="free"
        data-testid={testId}
        disabled={disabled}
        onClick={onOpen}
        className="relative flex w-full flex-wrap items-center gap-2.5 text-start"
      >
        <span data-ui="floor-tile-name">{table.name}</span>
        {seats}
        <span className="flex-1" />
        <span data-ui="floor-tile-free">{t((m) => m.paperFloor.freeLabel)}</span>
        {canOpen ? (
          <span data-ui="floor-card-act" data-tone="quiet">
            {t((m) => m.paperFloor.actOpen)}
          </span>
        ) : null}
        {askBadges ? <span className="basis-full">{askBadges}</span> : null}
      </button>
    );
  }

  const label =
    card.kind === "waiting"
      ? facts.pendingCount > 0
        ? t(
            countedKey(facts.pendingCount, {
              one: (m) => m.pos.pendingCountOne,
              two: (m) => m.pos.pendingCountTwo,
              few: (m) => m.pos.pendingCountFew,
              many: (m) => m.pos.pendingCount,
            }),
            { count: facts.pendingCount },
          )
        : t((m) => m.paperFloor.statusWaiting)
      : card.kind === "ready"
        ? t((m) => m.paperFloor.statusReady)
        : card.kind === "stale"
          ? t((m) => m.paperFloor.cardStale)
          : card.kind === "bill"
            ? !facts.open && facts.chequeCount > 1
              ? t(
                  countedKey(facts.chequeCount, {
                    one: (m) => m.pos.tableChequeCountOne,
                    two: (m) => m.pos.tableChequeCountTwo,
                    few: (m) => m.pos.tableChequeCountFew,
                    many: (m) => m.pos.tableChequeCount,
                  }),
                  { count: facts.chequeCount },
                )
              : t((m) => m.paperFloor.cardBill)
            : card.kind === "prebill"
              ? t((m) => m.paperFloor.cardPrebill)
              : card.kind === "kitchen"
                ? t((m) => m.paperFloor.statusPreparing)
                : card.kind === "eating"
                  ? t((m) => m.paperFloor.statusEating)
                  : t((m) => m.paperFloor.cardSeated);

  const time =
    card.minutes === null
      ? null
      : card.minutes >= 60
        ? t((m) => m.paperFloor.sinceHours, { count: Math.floor(card.minutes / 60) })
        : t((m) => m.paperFloor.since, { count: card.minutes });

  const action =
    card.kind === "waiting"
      ? { label: t((m) => m.paperFloor.actReview), tone: "primary" }
      : card.kind === "ready"
        ? { label: t((m) => m.paperFloor.actServe), tone: "primary" }
        : card.kind === "stale"
          ? { label: t((m) => m.paperFloor.actPrint), tone: "primary" }
          : card.kind === "bill"
            ? { label: t((m) => m.paperFloor.actCollect), tone: "primary" }
            : card.kind === "prebill"
              ? { label: t((m) => m.paperFloor.actPrint), tone: "quiet" }
              : card.kind === "seated"
                ? { label: t((m) => m.paperFloor.actTakeOrder), tone: "primary" }
                : null;

  const live = facts.open !== null;
  const owes = facts.due > 0.004;
  const dueLabel =
    !owes && facts.paidSoFar > 0
      ? t((m) => m.paperFloor.paid)
      : money(owes ? facts.due : 0);
  const plates = facts.plates;
  const showBar =
    live && plates !== null && plates.chargeable > 0 && card.kind !== "bill" && card.kind !== "stale";
  const liveBills = [...table.openInvoices, ...table.closedInvoices];
  const billNumber = liveBills.length === 1 ? `#${liveBills[0].dailySequence}` : null;
  const Icon = CARD_ICON[card.kind];

  return (
    <button
      type="button"
      data-ui="floor-tile"
      data-state={facts.status}
      data-card={card.kind}
      data-check={facts.check?.state}
      data-late={card.late ? "true" : undefined}
      data-testid={testId}
      disabled={disabled}
      onClick={onOpen}
      className="relative flex w-full flex-col items-stretch gap-2.5 overflow-hidden text-start"
    >
      <span className="flex items-center gap-2">
        <span data-ui="floor-tile-name">{table.name}</span>
        {seats}
        {billNumber ? (
          <span
            data-ui="floor-tile-seats"
            data-kind="bill"
            data-testid={`${testId}-bill-number`}
            className="whitespace-nowrap tabular-nums"
          >
            {billNumber}
          </span>
        ) : null}
      </span>

      <span
        data-ui="floor-card-pill"
        data-kind={card.kind}
        data-testid={facts.check ? `${testId}-check-${facts.check.state}` : `${testId}-status`}
        className="flex items-center gap-1.5"
      >
        <Icon aria-hidden="true" size={14} strokeWidth={1.9} className="shrink-0" />
        <span className="min-w-0">{label}</span>
        {time ? (
          <span
            data-ui="floor-card-time"
            data-late={card.late ? "true" : undefined}
            data-testid={`${testId}-since`}
            className="ms-auto whitespace-nowrap tabular-nums"
          >
            {time}
            {card.late ? <span className="sr-only"> · {t((m) => m.paperFloor.late)}</span> : null}
          </span>
        ) : null}
      </span>

      {live ? (
        <FloorTileKitchenLine plates={plates} late={facts.late} testId={testId} />
      ) : null}

      {showBar && plates ? (
        <span className="flex flex-col gap-1">
          <span data-ui="floor-card-bar" aria-hidden="true">
            <i style={{ width: `${Math.round((plates.served / plates.chargeable) * 100)}%` }} />
          </span>
          <span data-ui="floor-tile-served">
            {t((m) => m.paperFloor.servedOf, {
              served: plates.served,
              total: plates.chargeable,
            })}
          </span>
        </span>
      ) : null}

      {owes && facts.paidSoFar > 0 ? (
        <span data-ui="floor-tile-paid">
          {t((m) => m.paperFloor.paidSoFar, { amount: money(facts.paidSoFar) })}
        </span>
      ) : null}

      {(live && facts.cheque) ||
      facts.selfSeated ||
      facts.hasPaymentClaim ||
      facts.cashierRequested ||
      facts.noCaptain ||
      facts.openBillCount > 1 ? (
        <span className="flex flex-wrap gap-1">
          {facts.openBillCount > 1 ? (
            <span data-ui="floor-tile-badge" data-tone="info" data-testid={`${testId}-bills`}>
              {t(
                countedKey(facts.openBillCount, {
                  one: (m) => m.pos.tableBillCountOne,
                  two: (m) => m.pos.tableBillCountTwo,
                  few: (m) => m.pos.tableBillCountFew,
                  many: (m) => m.pos.tableBillCount,
                }),
                { count: facts.openBillCount },
              )}
            </span>
          ) : null}
          {live && facts.cheque ? (
            <span data-ui="floor-tile-badge" data-tone="warning">
              {t((m) => m.pos.tableChequeOut)}
            </span>
          ) : null}
          {facts.selfSeated ? (
            <span data-ui="floor-tile-badge" data-tone="info" data-testid={`${testId}-self-seated`}>
              {t((m) => m.pos.selfSeatedBadge)}
            </span>
          ) : null}
          {facts.cashierRequested ? (
            <span data-ui="floor-tile-badge" data-tone="warning" data-testid={`${testId}-cashier-requested`}>
              {t((m) => m.pos.cashierWaitingBadge)}
            </span>
          ) : null}
          {facts.hasPaymentClaim ? (
            <span data-ui="floor-tile-badge" data-tone="danger" data-testid={`${testId}-payment-claim`}>
              {t((m) => m.pos.paymentClaimBadge)}
            </span>
          ) : null}
          {facts.noCaptain ? (
            <span data-ui="floor-tile-badge" data-testid={`${testId}-no-captain`}>
              {t((m) => m.captain.none)}
            </span>
          ) : null}
        </span>
      ) : null}

      {askBadges}

      <span className="flex-1" />
      <span data-ui="floor-card-foot" className="flex items-center gap-2">
        <span data-ui="floor-tile-due" className="whitespace-nowrap tabular-nums">
          {dueLabel}
        </span>
        <span className="flex-1" />
        {action ? (
          <span data-ui="floor-card-act" data-tone={action.tone} data-testid={`${testId}-action`}>
            {action.label}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/** The "Needs you now" groups, in the board's order, and their dot colour. */
export const FLOOR_NEED_GROUPS = ["ready", "bill", "seated", "kitchen", "free"] as const;
export type FloorNeedGroup = (typeof FLOOR_NEED_GROUPS)[number];

/**
 * THE "تحتاجك الآن" BAR (2026-10 floor): how many tables are in each state,
 * each a toggle that narrows the floor to it, and how many are late. Tables
 * that need nothing (eating) are not counted.
 */
export function PaperFloorNeeds({
  counts,
  late,
  filter,
  onFilter,
}: {
  counts: Record<FloorNeedGroup, number>;
  late: number;
  filter: FloorNeedGroup | null;
  onFilter: (next: FloorNeedGroup | null) => void;
}) {
  const t = useTranslate();
  const label: Record<FloorNeedGroup, string> = {
    ready: t((m) => m.paperFloor.groupReady),
    bill: t((m) => m.paperFloor.groupBill),
    seated: t((m) => m.paperFloor.groupSeated),
    kitchen: t((m) => m.paperFloor.groupKitchen),
    free: t((m) => m.paperFloor.groupFree),
  };
  return (
    <section
      data-ui="floor-needs"
      aria-label={t((m) => m.paperFloor.needsYouLabel)}
      className="no-print flex flex-wrap items-center gap-2"
    >
      <span data-ui="floor-needs-title">{t((m) => m.paperFloor.needsYou)}</span>
      {FLOOR_NEED_GROUPS.map((group) => (
        <button
          key={group}
          type="button"
          data-ui="floor-need"
          data-group={group}
          aria-pressed={filter === group}
          data-testid={`floor-need-${group}`}
          onClick={() => onFilter(filter === group ? null : group)}
          className="inline-flex items-center gap-2"
        >
          <span aria-hidden="true" data-ui="floor-need-dot" />
          <b className="tabular-nums">{counts[group]}</b>
          <span>{label[group]}</span>
        </button>
      ))}
      <span className="flex-1" />
      {late > 0 ? (
        <span data-ui="floor-needs-late" data-testid="floor-needs-late">
          {t((m) => m.paperFloor.lateTables, { count: late })}
        </span>
      ) : null}
    </section>
  );
}

/** The board's colour key, under the floor. */
export function PaperFloorLegend() {
  const t = useTranslate();
  const items: [string, string][] = [
    ["ready", t((m) => m.paperFloor.groupReady)],
    ["bill", t((m) => m.paperFloor.groupBill)],
    ["seated", t((m) => m.paperFloor.cardSeated)],
    ["kitchen", t((m) => m.paperFloor.groupKitchen)],
    ["eating", t((m) => m.paperFloor.legendEating)],
    ["late", t((m) => m.paperFloor.legendLate)],
  ];
  return (
    <div
      data-ui="floor-key"
      aria-label={t((m) => m.paperFloor.legendLabel)}
      className="no-print flex flex-wrap items-center gap-3.5"
    >
      {items.map(([group, text]) => (
        <span key={group} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" data-ui="floor-need-dot" data-group={group} />
          {text}
        </span>
      ))}
    </div>
  );
}
